import { ContentService } from '@blk/cwp-ng-framework';
import { Memoize } from '@ddt-web/generic-utils';
import * as cloneDeep from 'lodash/cloneDeep';
import * as get from 'lodash/get';
import * as isEmpty from 'lodash/isEmpty';
import * as isNil from 'lodash/isNil';

import { CellData } from '../models/cell-data';
import { ColumnConfig } from '../models/column-config';
import { ColumnText } from '../models/column-text';
import { ColumnExtractor } from '../models/extractors/column-extractor';
import { CellRow } from '../models/interfaces/component.interface';
import { CellDataCondition, CellRowCondition } from '../models/interfaces/tab.interface';
import { TabData } from '../models/tab-data';
import { TableData } from '../models/table-data';

export class CellUtil {
	public static getCellRow(cells: CellData[]): CellRow {
		const rowData = (cells ? cells : []) as CellRow;
		rowData.field = {};
		if (cells) {
			cells.forEach(cell => {
				if (cell && cell.config && cell.config.id) {
					rowData.field[cell.config.id] = cell;
				}
			});
		}
		return rowData;
	}

	public static filterRow(cells: CellData[]): CellData[] {
		return cells.filter(cell => get(cell, 'config.visible'));
	}

	public static getById<T>(collection: T[], id: string): T {
		if (collection instanceof Array) {
			return collection.find(element => get(element, 'config.id') === id);
		}
		return null;
	}

	public static getIndex<T>(collection: T[], id: string): number {
		if (collection instanceof Array && collection.length > 0) {
			return collection.findIndex(element => get(element, 'config.id') === id);
		}
		return null;
	}

	public static getCellsById<T>(collection: T[][] = [], id: string): T[] {
		return collection.map(array => this.getById(array, id));
	}

	public static findByExpression(data: any, expressions: Expression[]): any {
		let result: any = JSON.parse(JSON.stringify(data));
		expressions.forEach(expression => {
			result = expression.prefix ? get(result, expression.prefix) : result;
			result = expression.id ? this.getById(result, expression.id) : result;
			result = expression.suffix ? get(result, expression.suffix) : result;
		});

		return result;
	}

	public static moveCellDataForTabData(tab: TabData, fromIndex: number, toIndex: number): TabData {
		tab.tableData = tab?.tableData?.map(tableData =>
			this.moveCellData(tableData, fromIndex, toIndex)
		);

		return tab;
	}

	public static moveCellData(tableData: TableData, fromIndex: number, toIndex: number): TableData {
		// move header
		let newHeadersX = cloneDeep(tableData.headersX) || [];
		const headerToMove = newHeadersX[fromIndex];
		newHeadersX = newHeadersX.filter(header => header !== headerToMove);
		newHeadersX.splice(toIndex, 0, headerToMove);

		// move cell data
		const newCells = cloneDeep(tableData.cells) || [];
		for (const row of newCells) {
			const cellToMove = row[fromIndex];
			row.splice(fromIndex, 1);
			row.splice(toIndex, 0, cellToMove);
		}

		return TableData.create({
			headersX: newHeadersX,
			headersY: tableData.headersY,
			cells: newCells,
			portfolioData: tableData.portfolioData,
			firstColumnTitle: tableData.firstColumnTitle,
			transposed: tableData.transposed
		});
	}

	public static translateColumnText(text: ColumnText, contentSvc: ContentService): ColumnText {
		if (text) {
			return new ColumnText(text.key, text.defaultText, contentSvc.get(text.key, text.defaultText), text.attributes);
		}
		return text;
	}

	public static translateColumnConfig<T extends ColumnExtractor>(config: ColumnConfig<T>, contentSvc: ContentService): ColumnConfig<T> {
		return config
			? new ColumnConfig(
				CellUtil.translateColumnText(config.label, contentSvc),
				CellUtil.translateColumnText(config.infoBubble, contentSvc),
				config.extractor,
				config.id,
				config.labelFormatter,
				config.formatters,
				config.visible
			)
			: config;
	}

	/**
	 * For the provided TabData, checks if tableData[0].cells has valid CellData values: not all rawValue undefined / null
	 */
	public static isValidCellData(tabData: TabData): boolean {
		let isValid: boolean = false;

		const cells: CellData[][] = get(tabData, 'tableData[0].cells');
		if (tabData && !tabData.error && !isEmpty(cells)) {
			const cellValidityArray = cells.map(cellArray => cellArray.some(cell => !isNil(cell.rawValue)));
			isValid = cellValidityArray.some(cellValidity => cellValidity);
		}

		return isValid;
	}

	/**
	 * @description isCellVisible: returns the cell's config visibility property.
	 */
	@Memoize()
	public static isCellVisible(cellData: CellData): boolean {
		return get(cellData, 'config.visible');
	}

	/**
	 * @description getFormattedCellValueById: returns the cell's formattedValue property.
	 */
	public static getFormattedCellValueById(cells: CellData[], id: string): string | number {
		const cell = this.getById(cells, id);
		return cell ? cell.formattedValue : '';
	}

	/**
	 * @description mergeArrays: returns the union of two 2D arrays by comparing the formattedValue CellData property
	 *
	 * @param a original array
	 * @param b array to merge with
	 */
	public static mergeArrays(a: CellData[], b: CellData[]): CellData[] {
		return b.reduce((retArray, item) => {
			if (retArray.find(col => col.formattedValue === item.formattedValue)) {
				return retArray;
			} else {
				return [...retArray, item];
			}
		}, [...a]);
	}

	/**
	 * Used by *CellWithCondition Transforms - given a portfolioCondition and a column condition, find the cell and apply the transform function to the cell
	 */
	public static findAndTransformCells(
		data: TabData,
		portfolioData: CellRow[],
		portfolioCondition: CellRowCondition,
		columnCondition: CellDataCondition,
		transformFunction: Function
	): TabData {
		const headers = data?.tableData?.[0]?.headersX;

		if (headers?.length) {
			const indexes = [];
			portfolioData.forEach((data, index) => {
				if (portfolioCondition.validate(data)) {
					indexes.push(index);
				}
			});
			const dataPointIdx = headers.findIndex(header => columnCondition.validate(header));
			data.tableData[0].cells.forEach((cell, index) => {
				if (indexes.includes(index) && cell[dataPointIdx]) {
					cell[dataPointIdx] = transformFunction(cell[dataPointIdx]);
				}
			});
		}
		return data;
	}
}

export class Expression {
	constructor(public id: string, public prefix: string = null, public suffix: string = null) { }
}
